import { IBusinessSignupOperationDTO, IBusinessType } from '@/models/business';
import { PaginationWithCount } from '@/models/general';
import { IPerformanceData } from '@/models/performance';
import { IHomeTip } from '@/models/tip';

import { app } from '@/main';
import BusinessTypes from '@/components/BusinessTypeSelector/typesENUM';

import APIService from '../api';
import { DashboardScreen } from './enums/dashboard-screen.enum';
import { ETutorial } from '@/models/tutorial';
import { EBusinessProviders } from '@/components/BusinessTypeSelector/business-providers.enum';

export default class BusinessService extends APIService {
  getBusinessInfo() {
    return this.get('/admin/business');
  }

  getBusinessAdditionalInfo() {
    return this.get('/admin/business/additional-details');
  }

  getBusinessPerformance() {
    return this.get<IPerformanceData>('/admin/business/dashboard');
  }

  getBusinessTips() {
    return this.get<PaginationWithCount<IHomeTip>>(
      '/admin/business/signup-steps?dashboard=true'
    );
  }

  getHomeOverview(params: any) {
    return this.get('/v2/admin/business/home', { params });
  }

  getHomeOverviewPerformance(
    reportType: string,
    dataType: string,
    params: any
  ) {
    return this.get(
      `/v2/admin/business/home/performance/${reportType}/${dataType}`,
      { params }
    );
  }

  getHomeOverviewGrapth(params: any) {
    return this.get('/v2/admin/business/home-graphs', { params });
  }

  getCoinExchange() {
    return this.get('/admin/award-coin/business/exchange');
  }

  updateProfile(data: object) {
    return this.put('/admin/business', data);
  }

  async updateSellCoins(sellCoins: boolean, screen: DashboardScreen) {
    const response = await this.put('/v2/admin/business/sell-coins-settings', {
      sellCoins,
      screen
    });

    return response;
  }

  updateMerchantId(data: object) {
    return this.put('/admin/business/merchant-id', data);
  }

  updateManager(data: object) {
    return this.put('/manager', data);
  }

  updateCoinsRule(data: object) {
    return this.put('/v2/admin/business/loyalty-settings', data);
  }

  getBasicReports() {
    return this.get('/admin/transaction/basic-reports');
  }

  getTransactionsByType(data: any, next?: string, type = 'all-transactions') {
    if (next) return this.get(next);
    let url = `/admin/transaction/${type}?`;

    url += this.dynamicResource(data);
    return this.get(url);
  }

  getDashboardInfo() {
    let url = '/admin/transaction/dashboard?';
    return this.get(url);
  }

  setOperationAnswers(data: IBusinessSignupOperationDTO) {
    return this.post<Record<'businessType', IBusinessType>>(
      '/admin/business/signup-operation',
      data
    );
  }

  isNewSellCoinsAvailable(): boolean {
    const businessInfo =
      app.$store.getters['business/getBusinessInfo'].business;

    // Sell coins flow is restricted only for US businesses
    if (businessInfo.countryIso2 !== 'US') {
      return false;
    }

    const isPhysical = [BusinessTypes.PHYSICAL, BusinessTypes.BOTH].includes(
      businessInfo.type
    );

    // Physical business is allowed to enable the sell coins
    if (isPhysical) {
      return true;
    }

    const currentProvider =
      app.$store.getters['merchant/getConnectedProviderWithWidgetSettings'];

    // Only online businesses with a provider is allowed to enable the sell coins
    const hasProviders =
      currentProvider &&
      currentProvider.connected &&
      [
        EBusinessProviders.WIX,
        EBusinessProviders.SHOPIFY,
        EBusinessProviders.BIGCOMMERCE,
        EBusinessProviders.WOOCOMMERCE
      ].includes(currentProvider.name);

    return hasProviders;
  }

  updateBusinessFlagTutorial({
    flag,
    state
  }: {
    flag: ETutorial;
    state: boolean;
  }) {
    return this.put(`/v2/admin/business/flag/${flag}`, { state });
  }

  sendGoals(slugs: string[]) {
    return this.put('/admin/business-goals', { slugs });
  }

  setCustomerInactivityDays(numberOfDays: number) {
    return this.put<{ inactiveCustomerDays: number }>(
      '/v2/admin/business/inactive-customer-settings',
      {
        inactiveCustomerDays: numberOfDays
      }
    );
  }

  getRewardStatusList() {
    return this.get('/merchant-connect/business/reward-status');
  }

  getRewardsSettings() {
    return this.get('/admin/rewards-settings');
  }

  putRewardsSettingsStatus(data: any) {
    return this.put('/v2/admin/business/loyalty-program/status', data);
  }
}
